/**
 * engine.ts — public surface + composition for the Speedtest engine
 * subscription, EMA smoothing, per-metric lifecycle, and phase-boundary
 * submission. Three sibling concerns live under `./engine/`:
 *
 *   - `./engine/progress.ts`     EMA + updateFromProgress + per-metric
 *                                state transitions + boundary-fire latches
 *   - `./engine/submission.ts`   final-resolution phase-boundary submission
 *   - `./engine/subscription.ts` engine subscription + lifecycle quartet
 *                                (start / abort / restart / reset)
 */

import type { Ref } from "vue";

import type {
    SpeedtestConfig,
    SpeedtestResults as EngineResults,
} from "@speedtest/index.ts";

import type {
    LiveTestData,
    MetricStates,
    PhaseCompletePayload,
} from "./types.ts";
import { createProgressTracker, type ProgressTracker } from "./engine/progress.ts";
import { createSubscriptionMachine } from "./engine/subscription.ts";

export interface EngineMachine {
    /** Reactive snapshot of the current test state. Null when idle. */
    data: Ref<LiveTestData | null>;
    /** True while a test is in flight. */
    isRunning: Ref<boolean>;
    /** Final results after test completion. Null until then. */
    results: Ref<EngineResults | null>;
    /** Progress tracker — owns the reactive `data` ref + per-phase boundary
     *  latches. Exposed so the DEV-only mock-results injector can drive the
     *  store to the terminal "complete" state via `applyFinal` exactly as
     *  the subscription machine's resolution branch does. Production code
     *  paths never read it (the meter/results surfaces read `data` /
     *  `results` directly). */
    progress: ProgressTracker;

    /** Wire the lifecycle hooks. `onEnd` fires once the engine resolves
     *  (success) or rejects (abort/error). */
    initialize: (onEnd?: (aborted: boolean) => void) => void;
    /** Patch the test parameters that take effect on the next `start()`. */
    configure: (overrides: Partial<SpeedtestConfig>) => void;

    start: () => void;
    abort: () => void;
    restart: () => void;
    reset: () => void;
}

export interface EngineMachineOptions {
    serverUrl: string;
    metricStates: MetricStates;
    submitPhaseSnapshot: (
        snapshot: PhaseCompletePayload,
        status: "partial" | "complete",
    ) => void;
}

export function createEngineMachine(options: EngineMachineOptions): EngineMachine {
    const { serverUrl, metricStates, submitPhaseSnapshot } = options;

    const progress = createProgressTracker({ metricStates, submitPhaseSnapshot });

    const subscription = createSubscriptionMachine({
        serverUrl,
        metricStates,
        progress,
        submission: { progress, submitPhaseSnapshot },
    });

    return {
        data: progress.data,
        isRunning: subscription.isRunning,
        results: subscription.results,
        progress,
        initialize: subscription.initialize,
        configure: subscription.configure,
        start: subscription.start,
        abort: subscription.abort,
        restart: subscription.restart,
        reset: subscription.reset,
    };
}
